/**
 * TanStack Query layer for editable (durable) field types — shared by both the
 * Custom Attributes management screen and the mapping-screen type editing.
 *
 * - `useCustomAttributes` — the org's loan-level custom fields + their durable type,
 *   coverage, and value context. Follows the isomorphic loader pattern: the route
 *   loader can `ensureRouteData(customAttributesQueryOptions())` and the component
 *   reads the same cache entry via `useCustomAttributes()`.
 * - `fetchRetypePreview` — a client-only, on-demand peek at how many of a field's
 *   stored values would not fit a proposed type (the modal calls it as the user
 *   chooses a new type).
 * - `useRetypeFields` — the retype mutation. On success it invalidates everything a
 *   type change touches: the custom-attributes list, the segment panel's fields +
 *   draft, and the portfolio loans + summary (so both screens and the list refresh).
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiClient } from '@repo/shared/client';
import {
  portfolioCustomAttributesContract,
  portfolioRetypePreviewContract,
  portfolioRetypeContract,
  type CustomAttributesResponse,
  type RetypePreviewResponse,
  type InferredType,
} from '@repo/shared/common';
import { contractQueryOptions, createMutationHook, MODELING_STATUS_KEY } from '~/lib/query';
import { PORTFOLIO_KEY, portfolioKeys } from './use-portfolio';
import { segmentKeys } from './use-segment';

/**
 * Prefix key matching every loan-list cache entry. `portfolioKeys.loans` is
 * parameterized by the full filter/sort params, so a prefix invalidation refreshes
 * all of them at once (partial query-key matching).
 */
const loansPrefixKey = [...PORTFOLIO_KEY, 'loans'] as const;

/** The inner preview shape (`RetypePreviewResponse` wraps it under `preview`). */
export type RetypePreview = RetypePreviewResponse['preview'];

/** Query key for the custom-attributes list — shared by the loader and the hook. */
export const customAttributesKey = [...PORTFOLIO_KEY, 'custom-attributes'] as const;

/**
 * `queryOptions` for the custom-attributes list. A route loader passes this to
 * `ensureRouteData`; the component reads the same definition via
 * {@link useCustomAttributes}, so loader prefetch + component read share one cache
 * entry (no waterfall, no double-fetch).
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- opaque queryOptions brand; the contract keeps callers typed.
export function customAttributesQueryOptions() {
  return contractQueryOptions(portfolioCustomAttributesContract, [...customAttributesKey]);
}

/** The org's loan-level custom fields + their durable type and value context. */
export function useCustomAttributes(): UseQueryResult<CustomAttributesResponse> {
  return useQuery(customAttributesQueryOptions());
}

/**
 * Peek at the consequence of retyping a field to `valueType`, on demand (client
 * only). Returns the inner preview ({ total, willNull, sample, settled }). The
 * modal calls this when the chosen TYPE differs from the field's current type.
 */
export async function fetchRetypePreview(
  key: string,
  valueType: InferredType,
): Promise<RetypePreview> {
  const { preview } = await apiClient(portfolioRetypePreviewContract, {
    query: { key, valueType },
  });
  return preview;
}

/** Query key for a retype consequence preview — keyed on (field, proposed type). */
export const retypePreviewKey = (key: string, valueType: InferredType): readonly unknown[] =>
  [...PORTFOLIO_KEY, 'retype-preview', key, valueType] as const;

/**
 * F126 — the retype consequence-preview as an explicit TRI-STATE. The modals used to swallow a
 * failed preview fetch (`.catch(() => setPreview(null))`) while leaving Save enabled, so a user
 * could commit a TYPE change that empties stored values without ever seeing the consequence. This
 * shared hook surfaces the failure as `status: 'error'` so both modals can show an alert AND
 * disable Save for a type change until the impact is known. `enabled` gates the fetch to a genuine
 * type change on an open modal; a subtype-only change passes `enabled: false` and stays `idle`
 * (no destructive preview, Save stays enabled).
 */
export interface RetypePreviewState {
  readonly status: 'idle' | 'loading' | 'error' | 'success';
  /** The preview when `status: 'success'`, else null. */
  readonly preview: RetypePreview | null;
}

export function useRetypePreview(
  key: string | null,
  valueType: InferredType | null,
  enabled: boolean,
): RetypePreviewState {
  const active = enabled && key !== null && valueType !== null;
  const query = useQuery({
    queryKey: retypePreviewKey(key ?? '', valueType ?? 'text'),
    queryFn: () => fetchRetypePreview(key as string, valueType as InferredType),
    enabled: active,
    // One retry, then honestly report the failure — the whole point of the tri-state.
    retry: 1,
    staleTime: 10_000,
  });
  if (!active) return { status: 'idle', preview: null };
  if (query.isError) return { status: 'error', preview: null };
  if (query.data !== undefined) return { status: 'success', preview: query.data };
  return { status: 'loading', preview: null };
}

/**
 * Retype one or more custom fields. Invalidates every read a type change can move:
 * the custom-attributes list, the segment panel's fields + draft, and the portfolio
 * loans + summary, so both consuming screens and the loan list refresh after a save.
 * A type change re-projects loans (dirtying the modeling dataset), so it also re-arms the
 * block-until-settled state via `MODELING_STATUS_KEY`.
 */
export const useRetypeFields = createMutationHook(portfolioRetypeContract, [
  customAttributesKey,
  segmentKeys.fields,
  segmentKeys.draft,
  loansPrefixKey,
  portfolioKeys.summary,
  MODELING_STATUS_KEY,
]);
